import React, { useEffect, useState } from 'react';
import Header from '../Common/AdminHeader';
import axios from 'axios';
import { Config } from '../../../API/config';
import { Toaster } from 'react-hot-toast';
import { FaSearch } from 'react-icons/fa';

// Interface Definitions
interface User {
    _id: string;
    name: string;
    phone: string;
}

export interface Cnotacts {
    _id: string;
    userId: User;
    message: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
}

// AdminContactsCard Component (Assumed Implementation)
const AdminContactsCard = ({ message, update }: { message: Cnotacts; update: () => void }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const maxPreviewLength = 100; // Define max length for preview

    const isMessageTooLarge = message.message.length > maxPreviewLength;
    const previewText = isMessageTooLarge
        ? `${message.message.substring(0, maxPreviewLength)}...`
        : message.message;

    return (
        <div className="w-72 p-4 bg-gray-100 rounded-lg shadow">
            <h3 className="text-lg font-semibold">{message.userId?.name || 'مجهول'}</h3>
            <h2 className="text-md">{message.userId.phone}</h2>
            <p className="text-sm text-gray-600 mt-2">
                {isExpanded ? message.message : previewText}
            </p>
            {isMessageTooLarge && (
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="mt-2 text-blue-500 hover:underline text-sm"
                >
                    {isExpanded ? 'عرض أقل' : 'عرض الرسالة كاملة'}
                </button>
            )}
            <p className="text-xs text-gray-400 mt-2">
                {new Date(message.createdAt).toLocaleDateString('ar-EG')}
            </p>
        </div>
    );
};

// Main ManageContacts Component
const ManageContacts = () => {
    const [messages, setMessages] = useState<Cnotacts[]>([]);
    const [filteredMessages, setFilteredMessages] = useState<Cnotacts[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState("");

    const getAllPendingMessages = async () => {
        try {
            setLoading(true);
            let { data } = await axios.get(Config.getAllContacts, { withCredentials: true });

            if (data.statusCode === 200) {
                setMessages(data.data.reverse());
                setFilteredMessages(data.data);
            }
        } catch (err) {
            console.error("Error fetching Messages data:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAllPendingMessages();
    }, []);

    // Filter messages based on search term
    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredMessages(messages);
        } else {
            const filtered = messages.filter((msg) =>
                msg.userId?.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredMessages(filtered);
        }
    }, [searchTerm, messages]);

    return (
        <div className="flex-1 overflow-auto relative z-10 bg-white">
            <Toaster />
            <Header title={"ادارة الرسائل"} />

            <main className="max-w-7xl mx-auto py-6 px-4 lg:px-8">
                <div className="relative mb-4 flex justify-end">
                    <input
                        type="text"
                        placeholder="ابحث عن رسالة..."
                        className="w-full md:w-1/2 p-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <FaSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                </div>
                <div className="flex flex-col md:flex-row flex-wrap justify-center gap-4">
                    {loading ? (
                        Array(6)
                            .fill(null)
                            .map((_, index) => (
                                <div
                                    key={index}
                                    className="w-72 p-4 bg-gray-100 rounded-lg shadow animate-pulse"
                                >
                                    <div className="h-5 w-1/2 bg-gray-300 rounded mb-2"></div>
                                    <div className="h-4 w-3/4 bg-gray-300 rounded mb-2"></div>
                                    <div className="h-24 w-full bg-gray-300 rounded"></div>
                                </div>
                            ))
                    ) : filteredMessages.length > 0 ? (
                        filteredMessages.map((msg, idx) => (
                            <AdminContactsCard message={msg} update={getAllPendingMessages} key={idx} />
                        ))
                    ) : (
                        <p className="text-center text-gray-600 w-full">لا توجد رسائل مطابقة</p>
                    )}
                </div>
            </main>
        </div>
    );
};

export default ManageContacts;